import { RefreshCw } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { FixtureResult } from '@/reports/bundles/types';
import { describeFixtureMiss } from '@/reports/bundles/fixtureMiss';
import { IconButton } from '../studioChrome';
import { FixtureMark } from './Explorer';
import { SidebarHeader, baseName } from './designerChrome';

/**
 * What the draft is held to, as it came out at the last save: each fixture,
 * the verdict it came to and the one it expects, and why one missed. Saving
 * runs them all again.
 */
export function TestsView({ results, compiles, saving, onOpen, onRun }: {
  results: Array<FixtureResult & { pending?: boolean }>;
  compiles: boolean;
  saving: boolean;
  onOpen: (path: string) => void;
  /** save, which compiles the draft and runs every fixture */
  onRun: () => void;
}) {
  const failing = results.filter((r) => !r.ok).length;
  // a fixture whose printed checks are still being made has not held yet
  const checking = results.some((r) => r.pending);
  return (
    <div className="flex h-full flex-col bg-muted/30">
      <SidebarHeader title={I18n.get('designerTests')}>
        <IconButton label={I18n.get('designerRunTests')} size="icon-xs" onClick={onRun} disabled={saving}><RefreshCw className={cn(saving && 'animate-spin')} /></IconButton>
      </SidebarHeader>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <p className={cn('px-3 pt-2.5 pb-1.5 text-xs', !compiles ? 'text-destructive' : failing ? 'text-red-700' : checking ? 'text-muted-foreground' : 'text-emerald-800')}>
          {!compiles
            ? I18n.get('designerTestsNotCompiled')
            : results.length === 0 ? I18n.get('reportBundleNoFixtures')
              : failing ? I18n.get('designerTestsFailing', { failing: String(failing), total: String(results.length) }) : checking ? I18n.get('designerTestsChecking') : I18n.get(results.length === 1 ? 'designerTestsHoldOne' : 'designerTestsHold', { total: String(results.length) })}
        </p>
        <ul aria-label={I18n.get('reportBundleFixtures')}>
          {results.map((r) => (
            <li key={r.file} className={cn('border-b-[1px] border-border/60 last:border-b-0', !r.ok && 'bg-red-50/60')}>
              <button type="button" onClick={() => onOpen(r.file)} className="flex w-full flex-col gap-0.5 px-3 py-1.5 text-left hover:bg-muted">
                <span className="flex items-center gap-2">
                  <FixtureMark result={r} />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">{baseName(r.file)}</span>
                  {(r.checks?.length ?? 0) > 0 && <span className="shrink-0 text-[0.65rem] text-muted-foreground">{I18n.get('designerChecksHeld', { held: String(r.checks!.filter((c) => c.ok).length), total: String(r.checks!.length) })}</span>}
                  <span className="shrink-0 text-[0.65rem] text-muted-foreground">{r.verdictLabel ?? '—'}</span>
                </span>
                {r.expected && <span className="pl-5 text-[0.7rem] text-muted-foreground">{I18n.get('reportBundleFixtureExpects', { expected: r.expected.verdictLabel ?? r.expected.verdict ?? '' })}</span>}
                {!r.ok && <span className="pl-5 text-[0.7rem] text-red-700 [overflow-wrap:anywhere]">{describeFixtureMiss(r)}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
